/**
 * An edit to a paired document reaches its session when typing pauses
 * (RFC 0301 S6).
 *
 * THE WINDOW THAT MADE THE EDIT DELIVERS IT, the way the window that wrote a
 * comment delivers that. No daemon watches documents, and none is needed for an
 * edit made here: this window already knows the moment a person typed.
 *
 * ONLY LOCAL CHANGES COUNT. A handle emits `change` for everything, including
 * an agent's patch arriving by sync. `isLocalChange` reads the actor on each
 * new change, so an agent's edit never wakes a session, its own or another's.
 * That is §3g's first guard.
 *
 * ONE DELIVERY PER PAUSE. The text before the first keystroke is kept, every
 * later keystroke restarts the wait, and when it runs out the whole pause is
 * delivered as one before and after.
 *
 * TYPING AGAIN WITHDRAWS IT (RFC 0301 S13). Four seconds is short on purpose:
 * when the thought was finished the agent answers fast. When it was not, the
 * next keystroke takes the signal back, the turn is cancelled if it has said
 * nothing yet, and the pause starts again from where the withdrawn one began,
 * so the next delivery is the whole change and not the tail of it. Past
 * RESUME_MS the turn is left alone: by then the person came back rather than
 * carried on.
 */

import type { Repo } from '@automerge/automerge-repo'
import {
  isLocalChange,
  isUndoChange,
  offerEdit,
  readDocumentContent,
  withdrawEdit,
} from '@mod/core/model'
import { useEffect, useRef } from 'react'
import { useSelection } from '../../services/sessions/selection'
import { holdDocument } from '../../services/world/repo/held-docs'

/** How long typing has to stop before an edit counts as a finished thought. */
export const QUIET_MS = 4000

/** How soon after a delivery a keystroke means "I was not finished". */
export const RESUME_MS = 30_000

/** The timing half, with no repo and no React, so it can be tested alone. */
export class EditPause {
  private before: string | null = null
  private after = ''
  private timer: ReturnType<typeof setTimeout> | null = null
  /** The last delivery, until a keystroke either resumes it or outlives it. */
  private delivered: { before: string; at: number } | null = null

  constructor(
    private readonly quietMs: number,
    private readonly onPause: (before: string, after: string) => void,
    private readonly opts: {
      /** A keystroke landed within `resumeMs` of a delivery: take it back. */
      onResume?: () => void
      resumeMs?: number
      now?: () => number
    } = {},
  ) {}

  /** A local change landed. Keeps the earliest `before`, restarts the wait. */
  note(before: string, after: string): void {
    if (this.before === null) {
      const last = this.delivered
      this.delivered = null
      const now = this.opts.now?.() ?? Date.now()
      if (last && now - last.at < (this.opts.resumeMs ?? RESUME_MS)) {
        // Not finished after all. Start again from where the withdrawn
        // delivery began, and ask for its turn back.
        this.before = last.before
        this.opts.onResume?.()
      } else {
        this.before = before
      }
    }
    this.after = after
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.settle(), this.quietMs)
  }

  /**
   * An undo landed (RFC 0301 S8). It is the person's write, so it would be
   * delivered like typing and the session would redo the edit just taken back.
   * The pause restarts from here instead, which drops it.
   *
   * WHAT IT COSTS: typing done in the same pause, before the undo, is dropped
   * with it. Undo and typing inside four seconds of each other is rare, and a
   * missed signal costs less than an edit made twice.
   */
  rebase(after: string): void {
    if (this.before === null) return
    this.before = after
    this.after = after
  }

  /** Deliver now: the pane is closing and the pause will never come. */
  flush(): void {
    if (this.timer) this.settle()
  }

  private settle(): void {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
    const before = this.before
    this.before = null
    if (before !== null && before !== this.after) {
      this.onPause(before, this.after)
      this.delivered = { before, at: this.opts.now?.() ?? Date.now() }
    }
  }
}

/**
 * Watch one plain document for local edits and offer each pause to the
 * sessions paired to it. `documentId` undefined turns it off, for an app view or
 * a record, whose structured fields are not prose a diff can show.
 */
export function useEditPause(
  repo: Repo,
  projectId: string,
  documentId: string | undefined,
): void {
  const { selection } = useSelection()
  const selectionRef = useRef(selection)
  selectionRef.current = selection

  useEffect(() => {
    if (!documentId) return
    let cancelled = false
    let stop: (() => void) | null = null
    // What the last pause put on which logs, so a keystroke can take it back.
    let outstanding: Promise<Array<{ sessionId: string; entryId: string }>> =
      Promise.resolve([])
    const pause = new EditPause(
      QUIET_MS,
      (before, after) => {
        const sel = selectionRef.current
        outstanding = offerEdit(repo as never, projectId, {
          documentId,
          before,
          after,
          ...(sel?.documentId === documentId
            ? { selectionLabel: sel.label }
            : {}),
        })
          .then((out) =>
            out.delivered.flatMap((d) =>
              d.outcome === 'dropped'
                ? []
                : [{ sessionId: d.sessionId, entryId: d.entryId }],
            ),
          )
          .catch(() => {
            // An edit that could not be offered is an edit nobody was told
            // about. The document itself is intact, so there is nothing to
            // recover.
            return []
          })
      },
      {
        onResume: () => {
          const take = outstanding
          outstanding = Promise.resolve([])
          void take.then((signals) => {
            for (const s of signals) {
              void withdrawEdit(repo as never, s.sessionId, s.entryId).catch(
                () => {
                  // A turn that could not be withdrawn runs; the next pause
                  // delivers the whole change again and it reads both.
                },
              )
            }
          })
        },
      },
    )
    // Listened to until the effect ends, so held until then (RFC 0317 §3d).
    const release = holdDocument(repo as never, documentId)
    void Promise.resolve(repo.find(documentId as never)).then((handle) => {
      if (cancelled) return
      const onChange = (p: {
        patchInfo?: { before?: unknown; after?: unknown }
      }) => {
        if (!isLocalChange(p.patchInfo?.before, p.patchInfo?.after)) return
        if (isUndoChange(p.patchInfo?.before, p.patchInfo?.after)) {
          pause.rebase(readDocumentContent(p.patchInfo?.after))
          return
        }
        pause.note(
          readDocumentContent(p.patchInfo?.before),
          readDocumentContent(p.patchInfo?.after),
        )
      }
      const h = handle as unknown as {
        on: (e: 'change', f: typeof onChange) => void
        off: (e: 'change', f: typeof onChange) => void
      }
      h.on('change', onChange)
      stop = () => h.off('change', onChange)
    })
    return () => {
      cancelled = true
      stop?.()
      release()
      pause.flush()
    }
  }, [repo, projectId, documentId])
}
